// src/components/dwh/snowflakeValidation.ts
//
// Client-side field validation for the BYO-Snowflake connect form. These are
// mirrors of the BFF's format gates (`account-dwh-snowflake.routes.ts`), which
// are themselves mirrors of the backend's `_SF_ACCOUNT_RE` / `_SF_IDENT_RE` and
// the DB CHECKs `ck_accounts_snowflake_*`.
//
// The server stays the authority — this layer only catches the obvious
// mistakes before a round trip, with the same wording the server would use.

import type { SnowflakeConnectFormValues } from "@/api/dataWarehouseSnowflake";

/** Org-account form: parts of [A-Za-z0-9_] joined by single hyphens or dots. */
export const SF_ACCOUNT_RE = /^[A-Za-z0-9_]+([.-][A-Za-z0-9_]+)*$/;
/** Snowflake identifier: letter/underscore first, then letters, digits, _ or $. */
export const SF_IDENT_RE = /^[A-Za-z_][A-Za-z0-9_$]{0,254}$/;
export const MAX_SF_IDENTIFIER_LENGTH = 255;

const ACCOUNT_FORMAT_MESSAGE =
  "Invalid Snowflake account identifier. Use the org-account form " +
  "(e.g. MYORG-ACCOUNT1) — letters, digits and underscores, with single " +
  "hyphens or dots between parts.";

function identifierFormatMessage(label: string): string {
  return (
    `Invalid Snowflake ${label} name. Use letters, digits, _ or $, starting ` +
    `with a letter or underscore (max ${MAX_SF_IDENTIFIER_LENGTH} characters).`
  );
}

/** Account identifier — required. Returns null when valid. */
export function validateSnowflakeAccount(raw: string): string | null {
  const value = raw.trim();
  if (!value) return "Account identifier is required";
  // Length is capped BEFORE the regex runs, same as the BFF.
  if (value.length > MAX_SF_IDENTIFIER_LENGTH) return ACCOUNT_FORMAT_MESSAGE;
  if (!SF_ACCOUNT_RE.test(value)) return ACCOUNT_FORMAT_MESSAGE;
  return null;
}

/**
 * Database / warehouse / role / user. Optional identifiers may be blank — the
 * backend then applies its own default — so only a non-empty value is checked.
 */
export function validateSnowflakeIdentifier(
  label: string,
  raw: string,
  opts?: { required?: boolean },
): string | null {
  const value = raw.trim();
  if (!value) {
    return opts?.required
      ? `${label.charAt(0).toUpperCase()}${label.slice(1)} is required`
      : null;
  }
  if (value.length > MAX_SF_IDENTIFIER_LENGTH) return identifierFormatMessage(label);
  if (!SF_IDENT_RE.test(value)) return identifierFormatMessage(label);
  return null;
}

/**
 * Shape-only check on a pasted key — the real RSA parse happens server-side.
 * Blank is valid: Verity then generates the keypair.
 */
export function validatePrivateKeyPem(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (!value.startsWith("-----BEGIN") || !value.includes("PRIVATE KEY-----")) {
    return "The pasted key is not a valid RSA private key (PKCS#8 PEM expected).";
  }
  return null;
}

export type SnowflakeFormErrors = Partial<
  Record<keyof SnowflakeConnectFormValues, string>
>;

/** All field errors at once. An empty object means the form may be submitted. */
export function snowflakeFormErrors(
  values: SnowflakeConnectFormValues,
): SnowflakeFormErrors {
  const errors: SnowflakeFormErrors = {};

  const account = validateSnowflakeAccount(values.account);
  if (account) errors.account = account;

  const database = validateSnowflakeIdentifier("database", values.database, {
    required: true,
  });
  if (database) errors.database = database;

  const warehouse = validateSnowflakeIdentifier("warehouse", values.warehouse);
  if (warehouse) errors.warehouse = warehouse;

  const role = validateSnowflakeIdentifier("role", values.role);
  if (role) errors.role = role;

  const user = validateSnowflakeIdentifier("user", values.user);
  if (user) errors.user = user;

  const key = validatePrivateKeyPem(values.privateKeyPem);
  if (key) errors.privateKeyPem = key;

  return errors;
}
